import { createSelector } from '@reduxjs/toolkit'
import { PROJECT_AUDIO_TYPES } from 'consts/audio'
import { TMultilineAudioItems } from 'store/types/audio'
import { PROJECT_MEDIA_TYPES } from 'consts'
import { TRootState } from 'store/types'
import { MediaStatus } from 'types/media'
import { isAudioFromGenres, isUploadingAudio } from 'utils/audio'
import { isBlobUrl } from 'utils'
import { editorSelectors } from 'store/features/editor/selectors'
import {
  convertAudioItemsToTimelineItems,
  convertMediaItemsToTimelineItems
} from 'utils/timeline'
import { isAudioItem } from 'utils/audio'
import {
  findActiveItemsBounds,
  generateBreakpoints,
  getCurrentLineItems,
  sortArraysByKey,
  getPendingMediaItems
} from './utils'
import { convertSizeToAspectRatio } from 'store/features/projectData/utils'
import { sortMediaItemsByStartTime } from 'utils/media'
import { IAudioMetaData } from 'types/audio'

const selector = (state: TRootState) => state.projectData

const data = createSelector(selector, state => state.present)

const mediaItems = createSelector(data, data => data.mediaItems)

const multilineAudios = createSelector(data, data => data.multilineAudios)

const visualMediaItems = createSelector(mediaItems, items =>
  items.filter(item => item.type !== PROJECT_MEDIA_TYPES.text)
)

const timelineItemsSortedByStartTime = createSelector(
  mediaItems,
  multilineAudios,
  (mediaItems, multilineAudios) => {
    const timelineMediaItems = convertMediaItemsToTimelineItems(mediaItems)
    const timelineAudioItems = convertAudioItemsToTimelineItems(multilineAudios)

    return sortArraysByKey('startTime', timelineMediaItems, timelineAudioItems)
  }
)

const hasMediaItems = createSelector(
  mediaItems,
  mediaItems => mediaItems.length > 0
)

const hasAudioItems = createSelector(
  multilineAudios,
  multilineAudios => multilineAudios.length > 0
)

const isLoading = createSelector(selector, state => state.isLoading)

const mediaItemsDuration = createSelector(
  mediaItems,
  mediaItems =>
    mediaItems.reduce((acc, mediaItem) => {
      const mediaItemEndTime = mediaItem.startTime + mediaItem.duration
      return Math.max(acc, mediaItemEndTime)
    }, 0) || 0
)

const multilineAudiosDuration = createSelector(
  multilineAudios,
  multilineAudios =>
    multilineAudios.reduce((acc, audio) => {
      const audioEndTime = audio.projectStart + audio.duration
      return Math.max(acc, audioEndTime)
    }, 0) || 0
)

const duration = createSelector(
  mediaItemsDuration,
  multilineAudiosDuration,
  (mediaItemsDuration, multilineAudiosDuration) => {
    return Math.max(mediaItemsDuration, multilineAudiosDuration)
  }
)

const defaultDimensions = createSelector(data, data => ({
  aspectRatio: data.defaultWidth / data.defaultHeight,
  aspectRatioString: convertSizeToAspectRatio(
    data.defaultWidth,
    data.defaultHeight
  ),
  width: data.defaultWidth,
  height: data.defaultHeight
}))

const firstLineAudios = createSelector(
  multilineAudios,
  multilineAudios =>
    multilineAudios?.filter(audio => audio.lineIndex === 0) || []
)

const firstLineAudiosWithSortedPositions = createSelector(
  firstLineAudios,
  firstLineAudios => sortArraysByKey('projectStart', firstLineAudios)
)

const firstLineLastAudioEndTime = createSelector(
  firstLineAudiosWithSortedPositions,
  firstLineAudiosWithSortedPositions => {
    const isFirstLineEmpty = firstLineAudiosWithSortedPositions.length === 0

    if (isFirstLineEmpty) {
      return 0
    }

    const lastAudio =
      firstLineAudiosWithSortedPositions[
        firstLineAudiosWithSortedPositions.length - 1
      ]

    return lastAudio.projectStart + lastAudio.duration
  }
)

const getLineEndTime = createSelector(
  [multilineAudios, firstLineLastAudioEndTime],
  (multilineAudios, firstLineEndTime) => (lineIndex: number) => {
    if (lineIndex === 0) {
      return firstLineEndTime
    }

    const lineAudios = getCurrentLineItems(multilineAudios, lineIndex)
    const sortedLineAudios = sortArraysByKey('projectStart', lineAudios)

    if (sortedLineAudios.length === 0) {
      return 0
    }

    const lastAudio = sortedLineAudios[sortedLineAudios.length - 1]
    return lastAudio.projectStart + lastAudio.duration
  }
)

const uploadingAudios = createSelector(
  multilineAudios,
  (multilineAudios: TMultilineAudioItems) =>
    multilineAudios?.filter(audio => isUploadingAudio(audio)) || []
)
const ttsAudios = createSelector(
  multilineAudios,
  (multilineAudios: TMultilineAudioItems) =>
    multilineAudios?.filter(audio => audio.type === PROJECT_AUDIO_TYPES.TTS) ||
    []
)

const soundTypeAudios = createSelector(
  multilineAudios,
  (multilineAudios: TMultilineAudioItems) =>
    multilineAudios?.filter(
      audio => audio.type === PROJECT_AUDIO_TYPES.SOUND
    ) || []
)

const pendingMediaItems = createSelector(mediaItems, mediaItems =>
  mediaItems.filter(
    item =>
      item.type !== PROJECT_MEDIA_TYPES.text &&
      (item.meta.status === MediaStatus.PENDING ||
        !item.meta.path ||
        isBlobUrl(item.meta.path))
  )
)

const hasPendingMediaItem = createSelector(
  pendingMediaItems,
  pendingMediaItems => pendingMediaItems.length > 0
)

const isExportDisabled = createSelector(
  duration,
  hasPendingMediaItem,
  (duration, hasPendingMediaItem) => duration === 0 || hasPendingMediaItem
)

export const pendingMediasFromPastAndFuture = createSelector(
  selector,
  state => {
    const past = getPendingMediaItems(state.past)

    const future = getPendingMediaItems(state.future)

    return [...past, ...future]
  }
)

const pendingAiImgMediaItems = createSelector(
  pendingMediaItems,
  pendingMediasFromPastAndFuture,
  (pendingMediaItems, pendingMediasFromPastAndFuture) =>
    [...pendingMediasFromPastAndFuture, ...pendingMediaItems].filter(
      item => item.type === PROJECT_MEDIA_TYPES.aiImage
    )
)

const pendingAIVideoMediaItems = createSelector(
  pendingMediaItems,
  pendingMediasFromPastAndFuture,
  (pendingMediaItems, pendingMediasFromPastAndFuture) =>
    [...pendingMediasFromPastAndFuture, ...pendingMediaItems].filter(
      item => item.type === PROJECT_MEDIA_TYPES.aiVideo
    )
)

const libAudioTitlesFromProject = createSelector(
  multilineAudios,
  multilineAudios =>
    multilineAudios.filter(isAudioFromGenres).map(audio => audio.title)
)
const activeMedia = createSelector(
  mediaItems,
  editorSelectors.activeTimelineItemId,
  (mediaItems, activeTimelineItemId) =>
    mediaItems.find(mediaItem => mediaItem.id === activeTimelineItemId)
)

const activeAudio = createSelector(
  multilineAudios,
  editorSelectors.activeTimelineItemId,
  (multilineAudios, activeTimelineItemId) =>
    multilineAudios.find(audio => audio.id === activeTimelineItemId)
)

const activeAudioTitle = createSelector(
  activeAudio,
  activeAudio => activeAudio?.title
)

const activeAudioDuration = createSelector(
  activeAudio,
  activeAudio => activeAudio?.mediaDuration
)

const activeAudioVolume = createSelector(
  activeAudio,
  activeAudio => activeAudio?.volume
)

// Undo/Redo selectors
const undoCount = createSelector(selector, state => state.past.length)
const redoCount = createSelector(selector, state => state.future.length)
const canUndo = createSelector(undoCount, count => count > 0)
const canRedo = createSelector(redoCount, count => count > 0)
const activeAudiosVolume = createSelector(
  multilineAudios,
  editorSelectors.activeAudioItems,
  (multilineAudios, activeAudioItems) => {
    return activeAudioItems.map(
      item => multilineAudios.find(audio => audio.id === item.id)?.volume
    )
  }
)

const modifiedActiveTimelineItemsForDragging = createSelector(
  mediaItems,
  multilineAudios,
  editorSelectors.activeTimelineItems,
  editorSelectors.timelineRulerSliceDuration,
  (
    mediaItems,
    multilineAudios,
    activeTimelineItems,
    timelineRulerSliceDuration
  ) => {
    const timelineMediaItems = convertMediaItemsToTimelineItems(mediaItems)
    const timelineAudioItems = convertAudioItemsToTimelineItems(multilineAudios)
    const notNullActiveTimelineItems = activeTimelineItems.filter(
      item => item.id !== null
    )

    const activeTimelineItemIds = activeTimelineItems.flatMap(item =>
      item.id !== null ? [item.id] : []
    )

    const modifiedItems = notNullActiveTimelineItems.map(activeItem => {
      const isAudio = isAudioItem(activeItem.type)
      const timelineItems = isAudio ? timelineAudioItems : timelineMediaItems
      const timelineItem = timelineItems.find(item => item.id === activeItem.id)

      if (!timelineItem) {
        return null
      }

      const { left, leftBoundary, rightBoundary, right } =
        findActiveItemsBounds(
          timelineItems,
          timelineItem,
          timelineRulerSliceDuration,
          activeTimelineItemIds
        )
      return {
        left,
        right,
        leftBoundary,
        rightBoundary,
        id: timelineItem.id,
        type: timelineItem.type
      }
    })

    return modifiedItems
  }
)
// IDs of selected media items when ALL are on the same zIndex (a same-line
// group). Returns [] when selection is single, contains audio, or spans lines —
// callers use that to fall back to the cross-line vanilla-DOM drag path.
const sameLineMultipleActiveMediaIds = createSelector(
  mediaItems,
  editorSelectors.activeMediaItems,
  (mediaItems, activeMediaItems) => {
    if (activeMediaItems.length <= 1) return [] as string[]
    const idToZIndex = new Map(mediaItems.map(item => [item.id, item.zIndex]))
    const zIndexes = new Set(
      activeMediaItems.map(a => (a.id ? idToZIndex.get(a.id) : undefined))
    )
    if (zIndexes.size !== 1 || zIndexes.has(undefined)) return []
    return activeMediaItems.map(a => a.id!)
  }
)

const processingItems = createSelector(selector, state => state.processingItems)

const magnetizingBreakpointsExcludingActive = createSelector(
  timelineItemsSortedByStartTime,
  editorSelectors.timelineRulerSliceDuration,
  editorSelectors.activeTimelineItemsIds,
  (
    timelineItemsSortedByStartTime,
    timelineRulerSliceDuration,
    activeItemsIds
  ) =>
    generateBreakpoints(
      timelineItemsSortedByStartTime.filter(
        item => !activeItemsIds.includes(item.id)
      ),
      timelineRulerSliceDuration
    )
)

const nextAvailableLineIndex = createSelector(
  multilineAudios,
  multilineAudios =>
    multilineAudios.reduce(
      (acc, audio) => Math.max(acc, audio.lineIndex + 1),
      0
    )
)

const firstVisualMediaItems = createSelector(visualMediaItems, mediaItems => {
  if (!mediaItems || mediaItems.length === 0) {
    return null
  }
  return sortMediaItemsByStartTime(mediaItems)[0]
})

export const isTimelineEmpty = createSelector(
  hasMediaItems,
  hasAudioItems,
  (hasMediaItems, hasAudioItems) => !hasMediaItems && !hasAudioItems
)

export const timelineUserSoundsMetaData = createSelector(
  selector,
  state => state.timelineUserSoundsMetaData
)

const makeGetSoundMetaDataByAssetId = () =>
  createSelector(
    (_: TRootState, assetId: string) => assetId,
    timelineUserSoundsMetaData,
    (assetId, timelineUserSoundsMetaData): IAudioMetaData | undefined =>
      timelineUserSoundsMetaData && timelineUserSoundsMetaData[assetId]
  )

const soundMetaDataByAssetId = makeGetSoundMetaDataByAssetId()
const isSoundsMetaDataEmpty = createSelector(
  timelineUserSoundsMetaData,
  metaData => !metaData
)

export const projectDataSelectors = {
  data,
  mediaItems,
  hasMediaItems,
  hasAudioItems,
  isLoading,
  duration,
  defaultDimensions,
  multilineAudios,
  uploadingAudios,
  ttsAudios,
  isExportDisabled,
  hasPendingMediaItem,
  pendingAiImgMediaItems,
  pendingAIVideoMediaItems,
  mediaItemsDuration,
  multilineAudiosDuration,
  libAudioTitlesFromProject,
  activeMedia,
  activeAudio,
  activeAudioTitle,
  activeAudioDuration,
  activeAudioVolume,
  firstLineLastAudioEndTime,
  getLineEndTime,
  undoCount,
  redoCount,
  canUndo,
  canRedo,
  activeAudiosVolume,
  timelineItemsSortedByStartTime,
  modifiedActiveTimelineItemsForDragging,
  magnetizingBreakpointsExcludingActive,
  processingItems,
  nextAvailableLineIndex,
  firstVisualMediaItems,
  isTimelineEmpty,
  soundTypeAudios,
  timelineUserSoundsMetaData,
  soundMetaDataByAssetId,
  isSoundsMetaDataEmpty,
  sameLineMultipleActiveMediaIds
}
